import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { Flex, ResponsiveSVG } from 'ui/acro'
import { Text } from 'ui/bend'
import { axios } from './../../../api'
import PlaylistInfo from './PlaylistInfo'
import PlaylistClass from './PlaylistClass'
import EmptyPlaylist from './EmptyPlaylist'
import LoaderPlaylist from './LoaderPlaylist'
import ReactSortable from 'react-sortablejs'
import chevronSVG from '../../../../../app/assets/images/chevron.svg'
import InfiniteScroll from 'react-infinite-scroller'
import PlayListClassAction from './PlayListClassAction'
import { useWindowResize } from 'hooks'
import { LIKED_ITEMS_SLUG } from 'components_v1/common'
import { useNavigate } from 'react-router-dom'

const BackButtonContainer = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`
const ContentWrapper = styled(Flex)`
  flex-direction: column;
  gap: 24px;
  ${(props) => props.theme.mediaQueries.small} {
    flex-direction: row;
  }
`
const DeleteText = styled(Text)`
  color: ${(props) => props.theme.colors.alert.medium};
  font-family: ${(props) => props.theme.fonts.Proxima};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px; /* 150% */
  letter-spacing: 0.352px;
  display: block;
  cursor: pointer;
  ${(props) => props.theme.mediaQueries.small} {
    display: none;
  }
`
const BackButtonText = styled(Text)`
  color: ${(props) => props.theme.colors.black};
  font-family: ${(props) => props.theme.fonts.Proxima};
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 133.333% */
  letter-spacing: 0.396px;
  display: none;
  ${(props) => props.theme.mediaQueries.small} {
    display: block;
  }
`

const PlaceholderBox = styled.div`
  width: 20%;
  height: 118px;
  @media (max-width: 1024px) {
    width: 30%;
  }
`

const PlaceholderContainer = styled.div`
  display: flex;
  margin-top: 24px;
  flex-direction: column;
  gap: 24px;
`

const PlaceholderFiller = styled.div`
  background: #ebebeb;
  padding-top: 56.25%;
`

const loadingCards = new Array(4).fill(null).map((_, idx) => (
  <PlaceholderContainer>
    <PlaceholderBox key={idx}>
      <PlaceholderFiller />
    </PlaceholderBox>
  </PlaceholderContainer>
))

const PlaylistDetailsPage = ({
  playlistObj,
  userIsAuthor,
  signedIn,
  source,
  membershipTier,
  totalItems
}) => {
  const [playlist, setPlaylist] = useState(playlistObj || {})
  const [listOfClasses, setListOfClasses] = useState([])
  const [hasMore, setHasMore] = useState(true)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedClassObject, setSelectedClassObject] = useState({})
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState()
  const { width } = useWindowResize()
  const [showLoader, setShowLoader] = useState(false)
  const isMobile = width < 640
  const [page, setPage] = useState(1)
  const [loadedCount, setLoadedCount] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    fetchData(false, true)
  }, [])

  const coverPhoto = useMemo(() => {
    if (playlist?.coverPhoto?.url) {
      return playlist?.coverPhoto?.url
    }
    if (playlist.image) {
      return playlist.image
    }
    return playlist?.firstEntry?.videoThumbnail
  }, [playlist])

  const {
    author,
    description,
    entryCount,
    title,
    totalDuration,
    uuid,
    following
  } = playlist

  const isLikedClassAndSeries = uuid === LIKED_ITEMS_SLUG
  const getPerPage = (reload) => {
    if (!reload) return 10
    if (reload === 'likes') return Math.min(30, loadedCount * 10)
    return loadedCount * 10
  }
  const fetchData = async (reloadAll = false, load = false) => {
    !(listOfClasses.length > 0) && setShowLoader(true)
    const more = reloadAll ? true : hasMore
    if (!more) return
    const last_evaluated_key = reloadAll ? null : lastEvaluatedKey
    const pNo = reloadAll ? 1 : page
    let params = {
      per_page: getPerPage(reloadAll),
      page: pNo,
      last_evaluated_key
    }
    try {
      const res = await axios.get(`/playlists/${playlist?.uuid}/entries`, {
        params,
        load
      })

      if (res.status === 200 && Array.isArray(res.data)) {
        let classList = []
        setListOfClasses((state) => {
          classList = pNo === 1 ? res.data : [...state, ...res.data]
          return classList
        })
        setPlaylist((state) => ({
          ...state,
          entryCount: res?.headers?.total,
          ...(pNo === 1 && {
            image: res?.data?.[0]?.videoThumbnail,
            firstEntry: {}
          })
        }))
        !reloadAll && setLoadedCount((state) => state + 1)
        if (classList.length >= Number(res?.headers?.total)) {
          setHasMore(false)
        } else {
          !reloadAll && setPage(page + 1)
        }
      } else if (res.status === 200 && Array.isArray(res?.data?.items)) {
        setListOfClasses((prev) =>
          !last_evaluated_key
            ? [...res?.data?.items]
            : [...prev, ...res?.data?.items]
        )
        setPlaylist((state) => ({
          ...state,
          entryCount: res?.data?.total
        }))
        !reloadAll && setLoadedCount((state) => state + 1)
        setLastEvaluatedKey(res?.data?.lastEvaluatedKey)
        setHasMore(Boolean(res?.data?.lastEvaluatedKey))
      }
    } catch (e) {
      console.error(e)
    } finally {
      setShowLoader(false)
    }
  }

  const onSortEnd = async ({ oldIndex, newIndex }) => {
    if (uuid === LIKED_ITEMS_SLUG) return
    const updatedList = [...listOfClasses]
    const [removedItem] = updatedList.splice(oldIndex, 1)
    updatedList.splice(newIndex, 0, removedItem)
    let planEntryIds = updatedList.map((classItem) => classItem.id)

    try {
      const res = await axios.post(`/playlists/${playlist?.uuid}/sort`, {
        plan_entry: planEntryIds
      })
      if (res.status === 200) {
        setListOfClasses(updatedList)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const getBackButtonText = () => {
    let BackButtonText = ''

    if (source === 'mypractice' || document.referrer.includes('/my/practice')) {
      BackButtonText = 'Back to My Playlists'
    } else if (
      source === 'playlists' ||
      document.referrer.includes('/playlists')
    ) {
      BackButtonText = 'Back to All Playlists'
    }

    return BackButtonText
  }

  const deletePlaylist = async () => {
    try {
      const res = await axios.delete(`/playlists/${uuid}`)
      if (res.status === 200) {
        window.location.href = document.referrer
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onEditPlaylist = async () => {
    try {
      const res = await axios.get(`/playlists/${uuid}`)
      if (res.status === 200) {
        setPlaylist((state) => ({ ...state, ...res.data }))
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onDeleteSuccess = (id) => {
    let newList = []
    setListOfClasses((state) => {
      newList = state.filter((item) => item.id !== id)
      return newList
    })
    if (uuid === LIKED_ITEMS_SLUG) {
      setLastEvaluatedKey(null)
      setPage(1)
      fetchData('likes')
    } else {
      fetchData('classes')
      setPlaylist((state) => ({
        ...state,
        entryCount: (state.entryCount || totalItems) - 1,
        image: newList?.[0]?.videoThumbnail
      }))
    }
  }

  if (showLoader) {
    return <LoaderPlaylist />
  }

  return (
    <Flex
      flexDirection='column'
      gap='16px'
      pt={['0px', '16px', '16px', '16px']}
      pl={['16px', '16px', '16px', '105px']}
      pr={['16px', '16px', '16px', '105px']}
      mb='100px'
    >
      {(document.referrer || ['mypractice', 'playlists'].includes(source)) && (
        <BackButtonContainer>
          <Flex
            alignItems='center'
            gap='8px'
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1)
              } else {
                navigate('/my/practice?tab=playlists', { replace: true })
              }
            }}
          >
            <ResponsiveSVG
              SVG={chevronSVG}
              width={['20px']}
              height={['20px']}
            />
            <BackButtonText>{getBackButtonText()}</BackButtonText>
          </Flex>
          {userIsAuthor && !isLikedClassAndSeries && (
            <DeleteText onClick={deletePlaylist}>Delete</DeleteText>
          )}
        </BackButtonContainer>
      )}
      <ContentWrapper>
        <PlaylistInfo
          coverPhoto={coverPhoto}
          description={description}
          classCount={entryCount ?? totalItems}
          author={author}
          title={title}
          totalDuration={totalDuration}
          uuid={uuid}
          userIsAuthor={userIsAuthor}
          following={following}
          signedIn={signedIn}
          onDelete={deletePlaylist}
          onEdit={onEditPlaylist}
        />
        {listOfClasses?.length > 0 ? (
          <InfiniteScroll
            pageStart={1}
            loadMore={() => fetchData()}
            hasMore={hasMore}
            loader={loadingCards}
            threshold={400}
            initialLoad={false}
            style={{ flex: '1 1 0%' }}
          >
            <ReactSortable
              onChange={(items, __, evt) => {
                onSortEnd(evt)
              }}
              options={{
                animation: 150
                // disabled: !sortable
              }}
              className='sortable-list'
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                flex: '1'
              }}
            >
              {listOfClasses?.map((classItem) => {
                return (
                  <PlaylistClass
                    classObject={classItem}
                    key={classItem.id}
                    data-id={classItem.id}
                    userIsAuthor={userIsAuthor}
                    uuid={uuid}
                    signedIn={signedIn}
                    setIsOpen={setIsOpen}
                    setSelectedClassObject={setSelectedClassObject}
                    isGridView={isMobile}
                    membershipTier={membershipTier}
                  />
                )
              })}
            </ReactSortable>
          </InfiniteScroll>
        ) : (
          <EmptyPlaylist />
        )}
      </ContentWrapper>
      <PlayListClassAction
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        dataObj={selectedClassObject}
        author={author}
        userIsAuthor={userIsAuthor}
        uuid={uuid}
        onDeleteSuccess={onDeleteSuccess}
        setShowLoader={setShowLoader}
      />
    </Flex>
  )
}
PlaylistDetailsPage.propTypes = {}

export default PlaylistDetailsPage
